import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { usePointTo } from "../../../agent";
import { refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { CoverageWord, DeliverableView, Id, SpotKind } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { useReportLocation } from "../../../live";
import { Banner, Button, Card, ConfirmDialog, EmptyState, Loading, Pill, SheetIcon } from "../../../ui";
import { FormError, Select, StringList, TextInput } from "../components/fields";
import { PageViewer } from "../components/PageViewer";
import { AddSpotDialog, SpotRow } from "../components/SpotRow";
import { useRefresh } from "../hooks";
import { locatorWords } from "../lib/locator";
import { deliverablePath } from "../lib/paths";
import { plural } from "../lib/text";
import { COVERAGE_WORDS, SPOT_KIND_WORDS, readerStatusWord, spotKindWord } from "../lib/words";

const PAGE_SIZE = 50;

/** The screen at /deliverables/:id: the pages, the spots read from them, and what could not be read. */
export function DeliverablePage() {
  const { id = "" } = useParams();
  const query = useApiQuery("deliverable", { id }, { enabled: !!id });
  useReportLocation({ screen: "deliverable_view" });
  if (query.isPending) return <Loading>Reading the document</Loading>;
  if (query.isError) {
    return (
      <Card>
        <EmptyState title="This document could not be opened">{refusalText(query.error)}</EmptyState>
      </Card>
    );
  }
  return <DeliverableContent view={query.data} />;
}

function DeliverableContent({ view }: { view: DeliverableView }) {
  const me = useCurrentMe();
  const editable = me.active?.roles.includes("engineer") ?? false;
  const [pageNo, setPageNo] = useState<number | null>(view.page_images[0] ?? null);
  const [kindFilter, setKindFilter] = useState<SpotKind | "">("");
  const [coverageFilter, setCoverageFilter] = useState<CoverageWord | "">("");
  const [onlyThisPage, setOnlyThisPage] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [selected, setSelected] = useState<Id | null>(null);
  const [pointedSentence, setPointedSentence] = useState<string | null>(null);
  const [marked, setMarked] = useState<{ page: number; region: [number, number, number, number] } | null>(null);

  const hasPictures = view.page_images.length > 0;
  const onPage = useApiQuery("spots", { id: view.id, page: 1, page_size: 200, page_no: pageNo }, { enabled: hasPictures && pageNo !== null });
  const list = useApiQuery("spots", {
    id: view.id,
    page: listPage,
    page_size: PAGE_SIZE,
    kind: kindFilter || null,
    coverage: coverageFilter || null,
    page_no: onlyThisPage ? pageNo : null,
  });

  usePointTo(
    (event) => {
      if (event.spot_id) {
        setSelected(event.spot_id);
        setPointedSentence(event.sentence);
      }
    },
    { screens: ["deliverable_view"] },
  );

  const counts = Object.entries(view.counts_by_kind).filter(([, n]) => n > 0);
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="deliverable-title">
        <h1 id="deliverable-title">
          <SheetIcon kind={view.deliverable_kind} label={view.document_kind.name} /> {view.name}
        </h1>
        <p className="element-meta">
          {view.document_kind.name} <Pill>{readerStatusWord(view.reader_status)}</Pill>
          {view.pages ? <span className="muted"> {plural(view.pages, "page")}</span> : null}
          <span className="muted"> {plural(view.spots_count, "spot")}</span>
        </p>
        {view.what_i_want && <p>What you want from it: {view.what_i_want}</p>}
        {view.newer_revision_of && (
          <p className="muted">
            A newer revision of <Link to={deliverablePath(view.newer_revision_of)}>an older document</Link>. The spots read from the older one are kept and marked out of date.
          </p>
        )}
        {pointedSentence && (
          <Banner tone="demo">
            <p className="agent-text">{pointedSentence}</p>
          </Banner>
        )}
        <Reading view={view} editable={editable} />
      </Card>

      {(counts.length > 0 || view.could_not_read.length > 0) && (
        <Card aria-labelledby="counts-title">
          <h2 id="counts-title">What was read</h2>
          {counts.length > 0 && (
            <ul className="chip-list" aria-label="Spots by kind">
              {counts.map(([kind, n]) => (
                <li key={kind}>
                  <Button
                    variant={kindFilter === kind ? "primary" : "secondary"}
                    aria-pressed={kindFilter === kind}
                    onClick={() => {
                      setKindFilter(kindFilter === kind ? "" : (kind as SpotKind));
                      setListPage(1);
                    }}
                  >
                    {spotKindWord(kind)}: {n}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {view.could_not_read.length > 0 && (
            <div className="could-not-read">
              <h3>What could not be read</h3>
              <ul>
                {view.could_not_read.map((entry, index) => (
                  <li key={index}>
                    {locatorWords(entry.locator)}: {entry.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {hasPictures && (
        <Card aria-labelledby="pages-title">
          <h2 id="pages-title">The pages</h2>
          <PageViewer
            deliverableId={view.id}
            pages={view.page_images}
            spots={onPage.data?.spots ?? []}
            selectedSpotId={selected}
            onSelectSpot={setSelected}
            onPageChange={setPageNo}
            canMark={editable}
            onMarked={(page, region) => setMarked({ page, region })}
            dpi={view.page_dpi ?? undefined}
          />
        </Card>
      )}

      <Card aria-labelledby="spots-title">
        <h2 id="spots-title">Spots</h2>
        <div className="filter-bar row" role="group" aria-label="Filters">
          <Select
            label="Kind of spot"
            value={kindFilter}
            placeholder="All kinds"
            options={Object.entries(SPOT_KIND_WORDS).map(([value, label]) => ({ value, label }))}
            onChange={(value) => {
              setKindFilter(value as SpotKind | "");
              setListPage(1);
            }}
          />
          <Select
            label="Where it stands"
            value={coverageFilter}
            placeholder="Any"
            options={Object.entries(COVERAGE_WORDS).map(([value, label]) => ({ value, label }))}
            onChange={(value) => {
              setCoverageFilter(value as CoverageWord | "");
              setListPage(1);
            }}
          />
          {hasPictures && (
            <label className="field__checkline">
              <input
                type="checkbox"
                checked={onlyThisPage}
                onChange={(event) => {
                  setOnlyThisPage(event.target.checked);
                  setListPage(1);
                }}
              />
              Only the page I am looking at
            </label>
          )}
        </div>
        {list.isPending ? (
          <Loading>Reading the spots</Loading>
        ) : list.isError ? (
          <FormError message={refusalText(list.error)} />
        ) : list.data.spots.length === 0 ? (
          <EmptyState title={view.spots_count === 0 ? "No spots yet" : "No spot matches these filters"}>
            {view.spots_count === 0
              ? view.reader_status === "uploaded"
                ? "The document has not been read yet. Read it, and its spots show here."
                : "Nothing was found to point at in this document."
              : "Change the filters to see more."}
          </EmptyState>
        ) : (
          <>
            <ul className="spot-list">
              {list.data.spots.map((spot) => (
                <SpotRow
                  key={spot.id}
                  spot={spot}
                  editable={editable}
                  selected={selected === spot.id}
                  onSelect={() => {
                    setSelected(spot.id);
                    const page = spot.locator.page;
                    if (page != null && view.page_images.includes(page)) setPageNo(page);
                  }}
                />
              ))}
            </ul>
            {pages > 1 && (
              <div className="row pager">
                <Button disabled={listPage <= 1} onClick={() => setListPage((p) => p - 1)}>
                  Previous
                </Button>
                <span className="muted">
                  Page {listPage} of {pages}
                </span>
                <Button disabled={listPage >= pages} onClick={() => setListPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {marked && <AddSpotDialog deliverableId={view.id} page={marked.page} region={marked.region} onClose={() => setMarked(null)} />}
    </div>
  );
}

/** Starting, confirming and cancelling the reading of the document (8.5). */
function Reading({ view, editable }: { view: DeliverableView; editable: boolean }) {
  const start = useApiCommand("start_intake");
  const confirm = useApiCommand("confirm_intake");
  const cancel = useApiCommand("cancel_intake");
  const refresh = useRefresh();
  const [pagesToRead, setPagesToRead] = useState("");
  const [items, setItems] = useState<string[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const job = view.job ?? null;
  const running = job?.state === "running" || job?.state === "waiting_for_yes";
  const canStart = editable && !running && view.reader_status !== "reading";

  const after = () => refresh("deliverable", "spots", "list_deliverables");

  return (
    <div className="reading">
      {job && (
        <div className="job" aria-label="How the reading is going">
          <p>
            {job.state === "waiting_for_yes"
              ? "Waiting for your yes"
              : job.state === "running"
                ? "Reading now"
                : job.state === "done"
                  ? "Reading is done"
                  : job.state === "cancelled"
                    ? "Reading was cancelled"
                    : job.state === "failed"
                      ? "Reading failed"
                      : job.state}
          </p>
          <progress max={Math.max(job.pages_total, 1)} value={job.pages_done} aria-label="Pages read" />
          <p className="muted">
            {job.pages_done} of {plural(job.pages_total, "page")} read. About {job.calls_estimated} model calls, {job.calls_used} used.
          </p>
        </div>
      )}
      {job?.needs_yes && editable && (
        <Banner>
          <p>
            Reading this takes about {job.calls_estimated} model calls, more than usual. Go ahead?
          </p>
          <div className="row">
            <Button
              variant="primary"
              busy={confirm.isPending}
              onClick={() => confirm.mutate({ id: view.id }, { onSuccess: after })}
            >
              Yes, read it
            </Button>
            <Button onClick={() => setCancelling(true)}>No, cancel the reading</Button>
          </div>
        </Banner>
      )}
      {running && !job?.needs_yes && editable && (
        <div className="row">
          <Button onClick={() => setCancelling(true)}>Cancel the reading</Button>
        </div>
      )}
      {canStart && (
        <details className="read-options">
          <summary>{view.reader_status === "uploaded" ? "Read it now" : "Read it again"}</summary>
          <TextInput label="Pages to read (optional)" hint="For example 1-3. Leave empty to read all of it." value={pagesToRead} onChange={setPagesToRead} />
          <StringList
            label="Items to read closely (for a drawing)"
            hint="Name the items whose connections should be read in the second pass."
            values={items}
            addLabel="Add an item"
            onChange={setItems}
          />
          <Button
            variant="primary"
            busy={start.isPending}
            onClick={() =>
              start.mutate(
                { id: view.id, pages: pagesToRead.trim() || null, second_pass_items: items.map((i) => i.trim()).filter(Boolean) },
                { onSuccess: after },
              )
            }
          >
            Start reading
          </Button>
        </details>
      )}
      <FormError message={start.error ? refusalText(start.error) : confirm.error ? refusalText(confirm.error) : null} />
      <ConfirmDialog
        open={cancelling}
        title="Cancel the reading?"
        message="What has been read so far stays usable. You can read the rest later."
        confirmLabel="Cancel the reading"
        cancelLabel="Keep reading"
        busy={cancel.isPending}
        error={cancel.error ? refusalText(cancel.error) : null}
        onCancel={() => setCancelling(false)}
        onConfirm={() =>
          cancel.mutate(
            { id: view.id },
            {
              onSuccess: () => {
                setCancelling(false);
                after();
              },
            },
          )
        }
      />
    </div>
  );
}
